import { useEffect, useState } from "react";
import { Check, Minus } from "lucide-react";
import { http } from "@/lib/api";
import { logWarn } from "@/lib/log";
import { MESSAGE_LEVEL, messagesFact } from "@/lib/pricing";

// Plan facts are built from the server's real plan settings (GET /billing/plans), never typed by hand.
export const TIER_NAMES = { free: "Free", starter: "Starter", plus: "Plus", unlimited: "Unlimited" };
const ORDER = ["free", "starter", "plus", "unlimited"];
const RANK = { free: 0, starter: 1, plus: 2, unlimited: 3 };
let cached = null;

export function usePlanConfig() {
  const [cfg, setCfg] = useState(cached);
  useEffect(() => {
    if (cached) return;
    http.get("/billing/plans").then((r) => { cached = r.data; setCfg(r.data); }).catch((e) => logWarn("Couldn't load plan settings", e));
  }, []);
  return cfg;
}

export const duration = (min) => (min >= 60 ? `${Math.floor(min / 60)} h${min % 60 ? ` ${min % 60} min` : ""}` : `${min} min`);
const has = (cfg, tier, feature) => RANK[tier] >= RANK[cfg.features[feature]];

export function videoFact(cfg, tier) {
  const v = cfg.video_limits[tier];
  return v.max_minutes ? `Videos up to ${v.max_minutes} minutes and ${v.max_mb >= 1024 ? `${v.max_mb / 1024} GB` : `${v.max_mb} MB`}` : "Videos of any length";
}

// Everything a tier includes, in plain words. `chat` overrides the first line (passes use their own duration).
export function tierFacts(cfg, tier, chat) {
  const d = cfg.daily_limits;
  return [
    chat || messagesFact(tier),
    `Listen and Mic: ${d.voice[tier]} uses a day`,
    d.draw[tier] ? `Draw it diagrams: ${d.draw[tier]} a day` : null,
    videoFact(cfg, tier),
    has(cfg, tier, "practice") ? "Practice sets, mock exams, flashcards and progress" : null,
    has(cfg, tier, "groups") ? "Create groups, share materials and host games" : "Join groups, read materials and play games",
    has(cfg, tier, "web") ? "Web research with cited sources" : null,
  ].filter(Boolean);
}

const cell = (v) => (v === true ? <Check className="h-4 w-4 text-sky-300 mx-auto" aria-label="Included" /> : v === false ? <Minus className="h-4 w-4 text-neutral-600 mx-auto" aria-label="Not included" /> : v);

function rows(cfg) {
  const d = cfg.daily_limits;
  return [
    ["Price", (t) => (t === "free" ? "$0" : `$${cfg.prices[`${t}_monthly`]}/mo or $${cfg.prices[`${t}_annual`]}/yr`)],
    ["Messages", (t) => MESSAGE_LEVEL[t]],
    ["Listen and Mic per day", (t) => d.voice[t]],
    ["Draw it diagrams per day", (t) => d.draw[t] || false],
    ["Longest video", (t) => (cfg.video_limits[t].max_minutes ? `${cfg.video_limits[t].max_minutes} min` : "Any length")],
    ["Practice, flashcards and progress", (t) => has(cfg, t, "practice")],
    ["Create groups and share materials", (t) => has(cfg, t, "groups")],
    ["Join groups and play games", () => true],
    ["Web research with sources", (t) => has(cfg, t, "web")],
  ];
}

export function PlanTable({ cfg, testid = "plan-table" }) {
  if (!cfg) return null;
  return (
    <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid={testid}>
      <table className="w-full min-w-[640px] text-sm">
        <thead><tr className="text-left text-neutral-400 border-b border-white/10">
          <th className="p-3 font-medium">Plan details</th>{ORDER.map((t) => <th key={t} className="p-3 font-medium text-center text-neutral-100">{TIER_NAMES[t]}</th>)}
        </tr></thead>
        <tbody>
          {rows(cfg).map(([label, fn]) => (
            <tr key={label} className="border-b border-white/[0.06] last:border-0" data-testid={`${testid}-row-${label.split(" ")[0].toLowerCase()}`}>
              <td className="p-3 text-neutral-400">{label}</td>{ORDER.map((t) => <td key={t} className="p-3 text-center text-neutral-200">{cell(fn(t))}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PassTable({ cfg }) {
  if (!cfg) return null;
  return (
    <ul className="grid sm:grid-cols-3 gap-3 text-sm" data-testid="pass-table">
      {Object.entries(cfg.pass_minutes).map(([t, min]) => (
        <li key={t} className="rounded-xl border border-white/10 p-4" data-testid={`pass-detail-${t}`}>
          <p className="font-medium text-neutral-100">{cfg.pass_names[t]} · ${cfg.prices[`${t}_day`]}</p>
          <p className="text-xs text-neutral-500 mt-1">{duration(min)} of unlimited chat with {TIER_NAMES[t]} features. One payment, no renewal.</p>
        </li>
      ))}
    </ul>
  );
}
